//Helpers
import {
	genNotificationItemAvatar,
	generateNotificationHTMLContent,
} from './notification-center-helpers';
import { decimal_to_hex } from 'src/helpers/utilities';
import { isBreaker } from 'src/helpers/interface_helpers';
import { navigateToDevice, navigateToSetting } from 'src/stores/ui/navigate-to';
import { DIMMERS_WITH_CALIBRATION } from 'src/constants';
//Types
import type {
	NotificationLogDeviceMeta,
	NotificationLogEntry,
	NotificationLogMeta,
	NotificationLogSceneMeta,
} from 'src/types/NotificationLog';
import { NotificationLogLevel, NotificationLogState } from 'src/types/NotificationLog';
import type { NotificationMenuItemPropsTypes } from '@Shelly/shelly-design-system/index';
import type { NotificationType } from 'src/helpers/notifications/DeviceNotificationBuilder';
import type { Device } from 'src/types/Device';

/**
 * Prefix used to namespace backend notification-log ids inside the shared
 * dropdown, so they never collide with device-notification ids (which are
 * `<deviceId>-<...>`) or promo ids.
 */
export const NOTIFICATION_LOG_ID_PREFIX = 'notiflog:';

/** Item shape consumed by `NotificationMenuItem`, mirroring the device mapper. */
export interface MappedNotificationLogItem {
	id: string;
	circleAvatar: { icon?: string; image?: string; backgroundColor?: string | null };
	isUnread: boolean;
	date: string | undefined;
	/** Epoch (ms) parsed from `ts`, used to sort the merged feed newest-first. */
	sortTimestamp: number;
	badge: NotificationMenuItemPropsTypes['badge'];
	content: string;
	actions: boolean;
	cta: { title: string; onClick: () => void };
	type: NotificationType;
}

/** Minimal i18n formatter signature (matches svelte-i18n's `$format`). */
type FormatMessage = (
	id: string,
	options?: { default?: string; values?: Record<string, unknown> },
) => string;

const LEVEL_TO_TYPE: Record<NotificationLogLevel, NotificationType> = {
	[NotificationLogLevel.Normal]: 'info',
	[NotificationLogLevel.Critical]: 'warning',
	[NotificationLogLevel.Fatal]: 'danger',
};

const LEVEL_TO_AVATAR: Record<NotificationLogLevel, { icon: string }> = {
	[NotificationLogLevel.Normal]: { icon: 'fas fa-bell text-primary' },
	[NotificationLogLevel.Critical]: { icon: 'fas fa-circle-exclamation text-warning' },
	[NotificationLogLevel.Fatal]: { icon: 'fas fa-triangle-exclamation text-danger' },
};

const LEVEL_TO_BADGE_KEY: Record<NotificationLogLevel, string> = {
	[NotificationLogLevel.Normal]: 'label_info',
	[NotificationLogLevel.Critical]: 'label_warning',
	[NotificationLogLevel.Fatal]: 'label_danger',
};

const LEVEL_TO_BADGE_CLASS: Record<NotificationLogLevel, string> = {
	[NotificationLogLevel.Normal]: 'text-primary',
	[NotificationLogLevel.Critical]: 'text-warning',
	[NotificationLogLevel.Fatal]: 'text-danger',
};

/**
 * Notification names that can navigate directly to a setting in the Safety tab.
 * Names not listed here still link to the corresponding device.
 */
const SAFETY_NOTIFICATION_NAMES = new Set(['overpower', 'overvoltage', 'overtemperature']);

/** Whether a mapped-item id belongs to the backend notification-log source. */
export const isNotificationLogItemId = (id: string): boolean =>
	typeof id === 'string' && id.startsWith(NOTIFICATION_LOG_ID_PREFIX);

/** Extract the backend row ID from a namespaced notification-log item id. */
export const parseNotificationLogRowId = (id: string): number =>
	Number(id.slice(NOTIFICATION_LOG_ID_PREFIX.length));

/**
 * Format the `ts` field (`"YYYY-MM-DD HH:mm:ss"`) for display.
 *
 * TODO(FE-1715): the backend has not confirmed the timezone of `ts`. We assume
 * UTC here; revisit once confirmed to avoid off-by-hours display.
 */
export const formatNotificationLogDate = (ts: string): string | undefined => {
	if (!ts) return undefined;
	const parsed = new Date(`${ts.replace(' ', 'T')}Z`);
	return Number.isNaN(parsed.getTime()) ? ts : parsed.toLocaleString();
};

/**
 * Parse the `ts` field into an epoch (ms) for sorting. Returns `0` for missing
 * or unparseable values (so they sink to the bottom).
 *
 * TODO(FE-1715): assumes UTC, pending backend confirmation of the timezone.
 */
export const notificationLogTimestamp = (ts: string): number => {
	if (!ts) return 0;
	const parsed = new Date(`${ts.replace(' ', 'T')}Z`);
	return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

/** Parse the backend JSON metadata without letting malformed rows break the feed. */
export const parseNotificationLogMeta = (meta?: string): NotificationLogMeta | undefined => {
	if (!meta) return undefined;

	try {
		const parsed = JSON.parse(meta) as NotificationLogMeta;
		return parsed && typeof parsed === 'object' ? parsed : undefined;
	} catch {
		return undefined;
	}
};

/** Whether parsed notification metadata identifies a device. */
const isDeviceMeta = (meta: NotificationLogMeta | undefined): meta is NotificationLogDeviceMeta =>
	!!meta &&
	'deviceId' in meta &&
	typeof meta.deviceId === 'string' &&
	(meta.channel === undefined || (Number.isInteger(meta.channel) && meta.channel >= 0));

/** Whether parsed notification metadata identifies a scene. */
const isSceneMeta = (meta: NotificationLogMeta | undefined): meta is NotificationLogSceneMeta =>
	!!meta && 'scnid' in meta && typeof meta.scnid === 'string' && typeof meta.scnname === 'string';

/**
 * Resolve a decimal backend ID and channel to the device key used by the app.
 * Multi-channel devices use `<hex>_<channel>`; channel 0 normally uses `<hex>`.
 */
export const resolveNotificationLogDevice = (
	meta: NotificationLogMeta | undefined,
	devices: Record<string, Device>,
): Device | undefined => {
	if (!isDeviceMeta(meta)) return undefined;

	const hexId = decimal_to_hex(meta.deviceId).toLowerCase();
	const channel = meta.channel ?? 0;
	const channelId = `${hexId}_${channel}`;
	const candidateIds = channel === 0 ? [hexId, channelId] : [channelId, hexId];

	return candidateIds.map((id) => devices[id]).find(Boolean);
};

/** Match the existing live-device notification targets for Safety settings. */
const getSafetySubSetting = (notificationType: string, device: Device): string | undefined => {
	switch (notificationType) {
		case 'overpower':
			return 'max_power_protection';
		case 'overvoltage':
			return isBreaker(device.type)
				? ''
				: DIMMERS_WITH_CALIBRATION.includes(device.type)
					? 'voltage_protection'
					: 'max_voltage_protection';
		default:
			return undefined;
	}
};

/** Build a device or Safety CTA from notification metadata. */
const getNotificationLogCta = (
	meta: NotificationLogMeta | undefined,
	device: Device | undefined,
	notificationType: string,
	formatMessage: FormatMessage,
): MappedNotificationLogItem['cta'] | undefined => {
	if (!device || !isDeviceMeta(meta)) return undefined;

	if (SAFETY_NOTIFICATION_NAMES.has(notificationType)) {
		return {
			title: formatMessage('go_to_safety_btn_title', { default: 'Go to Safety Tab' }),
			onClick: () =>
				navigateToSetting(device.id, 'safety', getSafetySubSetting(notificationType, device)),
		};
	}

	return {
		title: formatMessage('navigate_to_device', { default: 'Go to device' }),
		onClick: () => navigateToDevice(device.id),
	};
};

/**
 * Build localized content from metadata instead of rendering the language that
 * happened to be active when the backend persisted the notification.
 *
 * Legacy saved API strings remain fallbacks for older rows, unknown notification
 * types, missing devices, or translations not yet available in the locale.
 */
const getLocalizedNotificationLogContent = (
	entry: NotificationLogEntry,
	meta: NotificationLogMeta | undefined,
	device: Device | undefined,
	formatMessage: FormatMessage,
): { title: string; message: string } => {
	if (device && isDeviceMeta(meta)) {
		const values = {
			deviceName: device.name,
			time: formatNotificationLogDate(entry.ts),
		};

		return {
			title: formatMessage(`${entry.type}_title_single`, {
				default: entry.title || entry.type,
				values,
			}),
			message: formatMessage(`${entry.type}_message_single`, {
				default: entry.text || '',
				values,
			}),
		};
	}

	if (isSceneMeta(meta)) {
		const values = { sceneName: meta.scnname };
		return {
			title: formatMessage('scene_notification_single', {
				default: entry.title || entry.type,
				values,
			}),
			message: formatMessage('scene_single', {
				default: entry.text || '',
				values,
			}),
		};
	}

	return { title: entry.title || entry.type, message: entry.text || '' };
};

/**
 * Map backend notification-log entries into dropdown items.
 *
 * Dismissed entries are filtered out (the "Clear" action sets `state=3`, and the
 * GET endpoint still returns them). `isUnread` and severity are driven entirely
 * by the server-side `state`/`level`, not by the localStorage read/dismiss stores.
 */
export const mapNotificationLogEntries = (
	entries: NotificationLogEntry[],
	formatMessage: FormatMessage,
	devices: Record<string, Device>,
): MappedNotificationLogItem[] => {
	return entries
		.filter((entry) => entry.state !== NotificationLogState.Dismissed)
		.map((entry) => {
			const level = LEVEL_TO_TYPE[entry.level] ? entry.level : NotificationLogLevel.Normal;
			const meta = parseNotificationLogMeta(entry.meta);
			const device = resolveNotificationLogDevice(meta, devices);
			const cta = getNotificationLogCta(meta, device, entry.type, formatMessage);
			const content = getLocalizedNotificationLogContent(entry, meta, device, formatMessage);

			return {
				id: `${NOTIFICATION_LOG_ID_PREFIX}${entry.id}`,
				circleAvatar: device ? genNotificationItemAvatar(device) : LEVEL_TO_AVATAR[level],
				isUnread: entry.state === NotificationLogState.Unseen,
				date: formatNotificationLogDate(entry.ts),
				sortTimestamp: notificationLogTimestamp(entry.ts),
				badge: {
					textClass: LEVEL_TO_BADGE_CLASS[level],
					value: formatMessage(LEVEL_TO_BADGE_KEY[level], { default: LEVEL_TO_TYPE[level] }),
				},
				content: generateNotificationHTMLContent(content.title, content.message),
				actions: !!cta,
				cta: cta || { title: '', onClick: () => {} },
				type: LEVEL_TO_TYPE[level],
			};
		});
};
